import styled, { css } from 'styled-components';
import { Typography } from '../../global/theme';
import { mobile_xl } from '../../global/mediaQueryMixin';
import { AlignCenter, FlexBetween, FlexCenter, LineClamp } from '../../global/commonStyle';

interface Props {
  isTaskGrid?: boolean;
  isOverdue?: boolean;
  color?: string;
}
const Card = styled.div<Props>`
  min-width: 200px;
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-8);
  background-color: var(--background-primary);
  ${(props: Props) =>
    props.isTaskGrid &&
    css`
      min-width: unset;
      border-radius: var(--border-radius-12);
      width: unset;
    `}
  &:hover {
    transition: all var(--transition-normal);
    border: 0.5px solid var(--text-placeholder);
  }
`;
const FirstCards = styled.div<Props>`
  padding: var(--space-8);
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  &:hover {
    .plus-icon {
      visibility: visible;
    }
  }
  ${(props: Props) =>
    props.isTaskGrid &&
    css`
      padding: var(--space-12);
    `}
`;

const Label = styled.p`
  ${Typography.body_md}
  color: var(--text-primary);
  ${LineClamp(2)}
  margin: var(--space-0);
  word-break: break-word;
  &.task-title {
    padding-block: var(--space-2);
  }
  span {
    text-transform: uppercase;
    margin-right: var(--space-6);
    color: var(--text-secondary);
    white-space: nowrap;
  }
  a {
    color: var(--text-link);
    text-decoration: underline;
    &:hover {
      text-decoration: none;
    }
  }
`;
const Bottom = styled.div`
  ${FlexBetween}
  gap: var(--space-6);
`;

const AssignGroup = styled.div`
  ul {
    ${AlignCenter}
    flex-wrap: wrap;
    list-style-type: none;
    padding: var(--space-0);
    margin: var(--space-0) var(--space-0) var(--space-0) var(--space-8);
    width: max-content;
  }

  li {
    margin-left: -9px;
    border-radius: var(--border-radius-full);
  }

  .plus-icon {
    visibility: hidden;
    z-index: var(--z-index-base);
    background-color: var(--background-muted);
    width: 20px;
    height: 20px;
    ${FlexCenter}
    border: 1px solid var(--background-secondary);
  }
  .blank-plus-icon {
    background-color: var(--background-muted);
    z-index: var(--z-index-base);
    width: 20px;
    height: 20px;
    ${FlexCenter}
    border: 1px solid var(--background-secondary);
  }
  .large-margin {
    margin-left: -64px;
  }
  .small-margin {
    margin-left: -20px;
  }
`;

const AvatarImage = styled.div`
  ${AlignCenter}
  border-radius: var(--border-radius-full);

  > div > img {
    max-width: 100%;
  }
`;

const Box = styled.div`
  border-top: 0.5px solid var(--border-primary);
`;
const Content = styled.div`
  padding: var(--space-8) var(--space-12);
  ${FlexBetween}
  gap: var(--space-8);
  cursor: pointer;
  ${mobile_xl(css`
    padding: var(--space-8) var(--space-12) var(--space-8) var(--space-8);
  `)}
`;
const LeftSection = styled.div`
  ${AlignCenter}
  gap: var(--space-6);
`;

const TodayText = styled.div`
  padding: var(--space-2) var(--space-5);
  border-radius: var(--border-radius-6);
  background: var(--background-muted);
  p {
    margin: var(--space-0);
    ${Typography.body_sm_medium}
    color: var(--text-primary)
  }
`;

const Days = styled.div`
  ${Typography.body_md}
  color: var(--text-secondary);
`;

const Dates = styled.div`
  ${Typography.body_md}
  color: var(--text-primary);
`;

const Top = styled.div<Props>`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  align-items: baseline;
  ${(props: Props) =>
    props.isTaskGrid &&
    css`
      gap: var(--space-6);
    `}
  position: relative;
  abbr {
    text-decoration: none;
  }
`;
const Group = styled.div`
  background-color: var(--neutral-secondary);
  padding: var(--space-2) var(--space-4);
  ${Typography.body_sm}
  color: var(--text-secondary);
  border-radius: var(--border-radius-4);
  &:hover {
    color: var(--text-primary);
  }
`;
const Milestone = styled.div`
  background-color: var(--neutral-secondary);
  padding: var(--space-1) var(--space-4);
  ${Typography.body_sm}
  color: var(--text-secondary);
  border-radius: var(--border-radius-4);
  margin-bottom: var(--space-2);
  cursor: pointer;
  &:hover {
    color: var(--text-primary);
  }
`;

const Topline = styled.div`
  ${FlexBetween}
  gap: var(--space-6);
  width: 100%;
  .fill-color {
    fill: var(--text-secondary);
  }
`;

const Check = styled.div`
  display: flex;
  gap: var(--space-6);
  align-items: flex-start;
`;
const Checkbox = styled.div`
  width: 24px;
  height: 24px;
  ${FlexCenter}
  border-radius: var(--border-radius-12);
  &:hover {
    background-color: var(--neutral-secondary);
    input {
      filter: brightness(var(--brightness-value));
    }
  }
`;
const Bottomright = styled.div`
  ${AlignCenter}
  gap: var(--space-6);
  span {
    ${AlignCenter}
    gap: var(--space-4);
  }
`;

const IconDiv = styled.div`
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  gap: var(--space-4);
  flex-wrap: wrap;
`;
const LabelIcon = styled.div<Props>`
  width: 6px;
  height: 6px;
  border-radius: var(--border-radius-full);
  margin: var(--space-3);
  ${(props: Props) =>
    props.color &&
    css`
      background-color: ${props.color};
    `}
`;
const Righttext = styled.div`
  ${Typography.body_sm}
  color: var(--text-primary);
  ${LineClamp(1)}
  word-break: break-all;
`;

const ColorIcon = styled.div`
  ${AlignCenter}
  padding: var(--space-2) var(--space-6) var(--space-2) var(--space-3);
  gap: var(--space-3);
  border-radius: var(--border-radius-full);
  border: 0.5px solid var(--border-primary);
`;
const IconDueDate = styled.div`
  width: 24px;
  height: 24px;
  .calendar-fill-color {
    fill: var(--text-secondary);
  }
  .calendar-due-fill-color {
    fill: var(--accent-error);
  }
`;
const TextDate = styled.p<Props>`
  ${Typography.body_md}
  color: var(--text-secondary);
  margin: var(--space-0);
  white-space: nowrap;
  ${(props: Props) =>
    props.isOverdue &&
    css`
      color: var(--accent-error);
    `}
  &.task-completed {
    color: var(--text-secondary);
  }
`;

const OverLayDiv = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const TooltipDiv = styled.div`
  ${AlignCenter}
  gap: var(--space-4);
`;

export {
  Card,
  FirstCards,
  Label,
  Bottom,
  AssignGroup,
  AvatarImage,
  Box,
  Content,
  Days,
  Dates,
  Top,
  Group,
  Topline,
  Check,
  Checkbox,
  Bottomright,
  Milestone,
  IconDiv,
  LabelIcon,
  Righttext,
  ColorIcon,
  IconDueDate,
  TextDate,
  OverLayDiv,
  TooltipDiv,
  LeftSection,
  TodayText
};
